/* eslint-disable complexity */
import { useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';
import { DpOutlinedInput } from '@/components/blocks/OutlinedInput';
import { PasswordInput } from '@/components/blocks/PasswordInput';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { useForm } from '@/utils/useForm';
import { REG_EX } from '@/utils/address';
import { formatCardNumber } from '@/utils/formatCardNumber';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { useClubDpLoyaltyDistributor } from '@/data/Content/ClubDp';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';

interface Props {
	onGoToClubDppoints: () => void;
	onClose: () => void;
}

const initialValues = {
	phone: EMPTY_STRING,
	cardId: EMPTY_STRING,
	email: EMPTY_STRING,
	password: EMPTY_STRING,
	code: EMPTY_STRING,
};

export const AccountSummaryLinkToCardDppointsForm: React.FC<Props> = ({
	onGoToClubDppoints,
	onClose,
}) => {
	const form = useForm(initialValues);

	const handleCcNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const sanitizedValue = value.replace(/[^0-9]/g, '');
		form.handleInputChange({
			target: { name: 'cardId', value: sanitizedValue },
		} as React.ChangeEvent<HTMLInputElement>);
	};

	const handleCcPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		const sanitizedValue = value.replace(/[^0-9]/g, '');
		form.handleInputChange({
			target: { name: 'phone', value: sanitizedValue },
		} as React.ChangeEvent<HTMLInputElement>);
	};

	const { loyaltyOtpDistributor, loyaltyCardLink, openUpdateCard, loading, openBlocked, openOtp } =
		useClubDpLoyaltyDistributor();

	const onSubmitOtpCardLinkWrapper = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const data = form.values;

			form.handleSubmit(async (prev) => {
				if (!data.code) {
					form.setError?.((prev) => ({ ...prev, code: true }));
					return;
				}

				await loyaltyCardLink({
					...prev,
					code: data.code,
				});

				onGoToClubDppoints();
				onClose();
			})(event);
		},
		[form, loyaltyCardLink, onGoToClubDppoints, onClose]
	);

	const onSubmitOtpDistributorWrapper = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const data = form.values;

			let hasError = false;

			if (data.cardId.length !== 16 || !REG_EX.ONLY_NUMBERS.test(data.cardId)) {
				form.setError?.((prev) => ({ ...prev, cardId: true }));
				hasError = true;
			}

			if (data.phone.length < 10 || !REG_EX.ONLY_NUMBERS.test(data.phone)) {
				form.setError?.((prev) => ({ ...prev, phone: true }));
				hasError = true;
			}

			if (!hasError) {
				form.handleSubmit(loyaltyOtpDistributor)(event);
			}
		},
		[form, loyaltyOtpDistributor]
	);

	const cardIdFormatted = formatCardNumber(form.values.cardId);
	const phoneFormatted = formatPhoneNumber(form.values.phone);

	if (openOtp) {
		return (
			<AdaptiveDialog open onClose={onClose}>
				<Stack
					spacing={2}
					sx={{ paddingBottom: '20px' }}
					component="form"
					ref={form.formRef}
					onSubmit={onSubmitOtpCardLinkWrapper}
				>
					<Stack direction="column">
						<Typography
							sx={{
								fontSize: '20px',
								fontWeight: 800,
								lineHeight: '1.6',
							}}
						>
							{'¡Ya casi terminas!'}
						</Typography>
						<Typography
							sx={{
								fontSize: '16px',
								fontWeight: 400,
								lineHeight: '1.6',
								color: '#666666',
							}}
						>
							{'Ingresa el código que acabamos de enviar a tu celular'}
						</Typography>
					</Stack>
					<Stack direction="row" spacing={2}>
						<DpOutlinedInput
							label="Codigo"
							placeholder="Ingresa el código"
							name="code"
							required
							type="text"
							inputProps={{ maxLength: 6 }}
							fullWidth
							error={form.error.code}
							helperText={form.error.code ? 'Ingresa un código válido' : ''}
							value={form.values.code}
							onChange={form.handleInputChange}
							disabled={loading}
						/>

						<DpButton variant="primary" type="submit" disabled={loading}>
							{'Continuar'}
						</DpButton>
					</Stack>
				</Stack>
			</AdaptiveDialog>
		);
	}

	if (openUpdateCard) {
		return (
			<AdaptiveDialog open onClose={onClose}>
				<Stack marginBottom="10px">
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: 800,
							lineHeight: '1.6',
						}}
					>
						{'Actualización de tarjeta Clubdppuntos'}
					</Typography>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 400,
							lineHeight: '1.6',
							color: '#666666',
						}}
					>
						{
							'Neciitas actualizar tu tarjeta Clubdppuntos, completa tu registro de información para continuar'
						}
					</Typography>
					<Stack direction="row" spacing={2} marginTop="20px">
						<DpButton variant="outlined" fullWidth onClick={onClose}>
							{'Cancelar'}
						</DpButton>
						<DpButton href="https://dpcardpuntos.com" target="_blank" variant="primary" fullWidth>
							{'Actualizar'}
						</DpButton>
					</Stack>
				</Stack>
			</AdaptiveDialog>
		);
	}

	if (openBlocked) {
		return (
			<AdaptiveDialog open onClose={onClose}>
				<Stack>
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: 800,
							lineHeight: '1.6',
						}}
					>
						{'Usuario bloqueado'}
					</Typography>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 400,
							lineHeight: '1.6',
							color: '#666666',
							marginBottom: '20px',
						}}
					>
						{
							'Has alcanzado el límite de intentos para registrar tu tarjeta, inténtalo de nuevo más tarde.'
						}
					</Typography>
					<DpButton onClick={onClose} variant="primary" fullWidth>
						{'Actualizar'}
					</DpButton>
				</Stack>
			</AdaptiveDialog>
		);
	}

	return (
		<AdaptiveDialog open onClose={onClose}>
			<Stack
				spacing={2}
				sx={{ paddingBottom: '20px' }}
				component="form"
				ref={form.formRef}
				onSubmit={onSubmitOtpDistributorWrapper}
			>
				<Stack direction="column">
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: 800,
							lineHeight: '1.6',
						}}
					>
						{'Registra tu tarjeta dppuntos dpvale'}
					</Typography>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 400,
							lineHeight: '1.6',
							color: '#666666',
						}}
					>
						{'Registra tu tarjeta y canjea tus puntos dpvale'}
					</Typography>
				</Stack>
				<DpOutlinedInput
					label="Número de tarjeta"
					placeholder="Ingresa tu número de tarjeta"
					name="cardId"
					required
					type="tel"
					inputProps={{ maxLength: 19 }}
					error={form.error.cardId}
					helperText={form.error.cardId ? 'Ingresa un número de tarjeta válido' : ''}
					value={cardIdFormatted}
					onChange={handleCcNumberChange}
					disabled={loading}
				/>
				<DpOutlinedInput
					label="Teléfono"
					placeholder="Ingresa tu número de teléfono"
					name="phone"
					required
					type="tel"
					inputProps={{ maxLength: 13 }}
					error={form.error.phone}
					value={phoneFormatted}
					helperText={form.error.phone ? 'El número de teléfono debe tener 10 dígitos' : ''}
					onChange={handleCcPhoneChange}
					disabled={loading}
				/>
				<DpOutlinedInput
					label="Correo electrónico"
					placeholder="Ingresa tu correo electrónico"
					name="email"
					required
					type="email"
					inputProps={{
						maxLength: 100,
						type: 'email',
						pattern: REG_EX.EMAIL.source,
					}}
					error={form.error.email}
					helperText={form.error.email ? 'Ingresa un correo electrónico válido' : ''}
					value={form.values.email}
					onChange={form.handleInputChange}
					disabled={loading}
				/>
				<PasswordInput
					label="Contraseña"
					placeholder="Ingresa tu contraseña"
					name="password"
					required
					inputProps={{
						maxLength: 40,
						pattern: REG_EX.PASSWORD.source,
					}}
					error={form.error.password}
					helperText={form.error.password ? 'La contraseña debe tener al menos 8 caracteres' : ''}
					value={form.values.password}
					onChange={form.handleInputChange}
					disabled={loading}
				/>
				<Stack direction="row" spacing={2}>
					<DpButton variant="outlined" fullWidth onClick={onClose} disabled={loading}>
						{'Cancelar'}
					</DpButton>
					<DpButton variant="primary" fullWidth type="submit" disabled={loading}>
						{'Enviar código'}
					</DpButton>
				</Stack>
			</Stack>
		</AdaptiveDialog>
	);
};
